import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, Wand2 } from "lucide-react";
import VersionPlayer from "@/components/creator/music/VersionPlayer";
import { VOCAL_JOB_PHASES } from "@/lib/studioApi";

function Result({ job, project, original }) {
  const made = (project.versions || []).find((v) => v.id === job.result_version_id);
  const s = job.stats;
  return (
    <div data-testid="replace-result" className="rounded-3xl border border-emerald-400/30 bg-emerald-500/[0.07] p-5">
      <p className="flex items-center gap-2 font-display text-lg font-semibold text-emerald-100"><CheckCircle2 className="h-5 w-5" /> Your voice now leads the song</p>
      {s?.voiced_sec ? <p data-testid="replace-result-stats" className="mt-1 text-[12px] text-white/55">Tuned {Math.round(s.voiced_sec)} s of singing · notes moved {s.avg_fix_cents} cents on average.</p> : null}
      {made ? (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div><p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-amber-200">Yours · new version</p><VersionPlayer id={`result-${made.id}`} url={made.audio_url} duration={made.duration} /></div>
          <div><p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/45">Original · AI lead</p><VersionPlayer id={`original-${original.id}`} url={original.audio_url} duration={original.duration} /></div>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3">
        <Link to={`/create/project/${project.id}`} data-testid="replace-result-project" className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.1] px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white hover:bg-white/[0.16]">Back to the project</Link>
        {made?.song_id ? <Link to={`/create/song/${made.song_id}`} data-testid="replace-result-open" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white">Master · Stems · Export <ArrowRight className="h-3 w-3" /></Link> : null}
      </div>
    </div>
  );
}

/** The Replace button, the live render progress, and the finished version side by side with the original. */
export default function ReplaceJob({ job, busy, canStart, onStart, project, original }) {
  const running = job?.status === "running";
  return (
    <div className="space-y-4">
      {job?.status === "done" ? <Result job={job} project={project} original={original} /> : null}
      {job?.status === "error" ? (
        <p data-testid="replace-error" className="flex items-start gap-2 rounded-2xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-100"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{job.error}</p>
      ) : null}
      {running ? (
        <div data-testid="replace-progress" data-phase={job.phase} className="rounded-2xl border border-amber-300/30 bg-amber-500/[0.08] px-4 py-4">
          <p className="flex items-center gap-2 text-sm text-amber-100"><Loader2 className="h-4 w-4 animate-spin" /> <span data-testid="replace-phase">{VOCAL_JOB_PHASES[job.phase] || "Working"}…</span></p>
          <p className="mt-1 text-[12px] text-white/55">Usually under a minute. The original version stays untouched — this makes a new one.</p>
        </div>
      ) : (
        <button type="button" data-testid="replace-start-btn" onClick={onStart} disabled={!canStart || busy}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-rose-500 px-7 py-3.5 font-display text-sm font-semibold text-[#05070f] shadow-[0_0_30px_rgba(251,191,36,0.25)] transition-transform duration-200 hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {job?.status === "done" ? "Make another version" : "Replace the lead vocal"}
        </button>
      )}
    </div>
  );
}
